import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles, alpha } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const WEEKDAYS = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];

const useStyles = makeStyles((theme) => ({
  strip: { display: "flex", alignItems: "center", gap: 6 },
  day: {
    flex: 1,
    minWidth: 0,
    height: 56,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    font: "inherit",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
    "&:disabled": { opacity: 0.45, cursor: "default" },
  },
  dayActive: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: "#fff",
  },
  dayWd: { fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" },
  dayNum: { fontSize: 16, fontWeight: 700 },
  navButton: {
    width: 32,
    height: 32,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 0,
    flexShrink: 0,
  },
  dateInput: {
    height: 32,
    boxSizing: "border-box",
    padding: theme.spacing(0, 1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    font: "500 13px Inter, Roboto, sans-serif",
    colorScheme: theme.palette.type,
  },
  slots: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
    gap: 6,
    marginTop: theme.spacing(1.25),
  },
  slot: {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    font: "500 13px Inter, Roboto, sans-serif",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  slotActive: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 700,
    "&:hover": { backgroundColor: theme.palette.primary.dark },
  },
  muted: { color: theme.palette.text.secondary },
}));

// Escolha de dia (faixa de 7 dias) e horário livre. Busca os horários em
// /appointments/slots; sem `userId` junta os de todos os profissionais do
// serviço (um botão por horário, escolhendo o primeiro profissional livre).
const SlotPicker = ({ serviceId, userId, date, onDateChange, value, onChange }) => {
  const classes = useStyles();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stripStart, setStripStart] = useState(() => moment(date).startOf("day"));

  // Mantém o dia escolhido sempre visível na faixa.
  useEffect(() => {
    const d = moment(date).startOf("day");
    setStripStart((s) => (d.isBefore(s) || d.isAfter(s.clone().add(6, "days")) ? d : s));
  }, [date]);

  useEffect(() => {
    if (!serviceId || !date) {
      setSlots([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get("/appointments/slots", {
        params: { serviceId, date, userId: userId || undefined },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const now = moment();
        const seen = new Set();
        const list = (data.slots || [])
          .filter((s) => moment(s.startAt).isAfter(now))
          .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
          .filter((s) => {
            if (seen.has(s.startAt)) return false;
            seen.add(s.startAt);
            return true;
          });
        setSlots(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setSlots([]);
          toastError(err);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [serviceId, userId, date]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => stripStart.clone().add(i, "days")),
    [stripStart]
  );
  const today = moment().startOf("day");

  return (
    <div>
      <div className={classes.strip}>
        <IconButton
          className={classes.navButton}
          aria-label="Dias anteriores"
          disabled={!stripStart.isAfter(today)}
          onClick={() => setStripStart((s) => moment.max(today, s.clone().subtract(7, "days")))}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        {days.map((d) => {
          const key = d.format("YYYY-MM-DD");
          const active = key === date;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              disabled={d.isBefore(today)}
              className={`${classes.day} ${active ? classes.dayActive : ""}`}
              onClick={() => onDateChange(key)}
            >
              <span className={classes.dayWd}>{WEEKDAYS[d.day()]}</span>
              <span className={classes.dayNum}>{d.date()}</span>
            </button>
          );
        })}
        <IconButton
          className={classes.navButton}
          aria-label="Próximos dias"
          onClick={() => setStripStart((s) => s.clone().add(7, "days"))}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <input
          type="date"
          aria-label="Escolher outra data"
          className={classes.dateInput}
          min={today.format("YYYY-MM-DD")}
          value={date}
          onChange={(e) => e.target.value && onDateChange(e.target.value)}
        />
      </div>

      {loading ? (
        <div style={{ padding: 16, textAlign: "center" }}>
          <CircularProgress size={22} />
        </div>
      ) : slots.length === 0 ? (
        <Typography variant="body2" className={classes.muted} style={{ marginTop: 12 }}>
          Nenhum horário livre neste dia{userId ? " para este profissional" : ""}. Tente outro dia.
        </Typography>
      ) : (
        <div className={classes.slots}>
          {slots.map((s) => {
            const active = value && moment(value.startAt).isSame(s.startAt);
            return (
              <button
                key={s.startAt}
                type="button"
                aria-pressed={!!active}
                className={`${classes.slot} ${active ? classes.slotActive : ""}`}
                onClick={() => onChange(s)}
              >
                {moment(s.startAt).format("HH:mm")}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SlotPicker;
